import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { Icon } from "~/features/ui/icon";
import { useFormState } from "~/features/ui/form/use-form-state";
import { type FormValues, useFields } from "~/features/newsletter/fields";
import { Input } from "~/features/ui/form/input";
import { Headline } from "~/features/ui/headline";
import { Button } from "~/features/ui/button";
import { useLocale } from "~/features/i18n/use-locale";
import { SuccessMessage } from "~/features/ui/form/success-message";
import { FieldError } from "~/features/ui/form/field-error";
import { validateRecaptcha, preloadRecaptcha } from "~/features/re-captcha/utils";
import Loading from "~/public/icons/loading.svg";
type Props = {
  text?: string;
  size?: keyof typeof sizes;
};
const sizes = {
  small: {
    "width": "100%",
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400",
    "@media (min-width: 1024px)": {
      "width": "33.333333%",
      "paddingRight": "4rem"
    }
  },
  large: {
    "width": "100%",
    "fontSize": "2rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.02rem",
    "fontWeight": "400",
    "@media (min-width: 1024px)": {
      "width": "50%",
      "paddingRight": "8rem",
      "fontSize": "2.4rem",
      "lineHeight": "1.3",
      "letterSpacing": "0.024rem",
      "fontWeight": "400"
    }
  }
} as const;
const translations = {
  submit: {
    en: "Subscribe",
    zh: "订阅",
    ja: "購読",
    ko: "구독"
  },
  message: {
    en: "Thank you!",
    zh: "谢谢！",
    ja: "ありがとうございます！",
    ko: "감사합니다!"
  }
} as const;
const MARKETO_FORM_ID = "1402";
export function Newsletter(props: Props) {
  const {
    text,
    size = "small"
  } = props;
  const [status, setStatus] = useFormState();
  const {
    register,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<FormValues>();
  const locale = useLocale();
  const router = useRouter();
  const fields = useFields(locale);
  const onSubmit = handleSubmit(async data => {
    setStatus({
      state: "LOADING"
    });
    const isRecaptchaValid = await validateRecaptcha();
    if (!isRecaptchaValid) {
      setStatus({
        state: "ERROR",
        message: "There was an issue with reCAPTCHA, please try again later."
      });
      return;
    }
    try {
      const res = await fetch("/api/submit-marketo-form", {
        method: "POST",
        body: JSON.stringify({
          fields: data,
          formId: MARKETO_FORM_ID,
          url: window.location.href
        })
      });
      if (res.status !== 200) {
        throw new Error("An error has occurred");
      }
      setStatus({
        state: "SUCCESS"
      });
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          formSubmitted: MARKETO_FORM_ID
        }
      }, undefined, {
        shallow: true
      });
    } catch (e: any) {
      console.error(e);
      setStatus({
        state: "ERROR",
        message: e.message
      });
    }
  });
  function renderField(field: (typeof fields)[number]) {
    return <Input name={field.name} error={errors[field.name]?.message} required={field.required} placeholder={field.placeholder} type={field.type} disabled={status.state === "LOADING"} register={register(field.name, {
      required: {
        value: true,
        message: field.message
      },
      pattern: {
        value: field.pattern,
        message: field.message
      },
      validate: value => {
        // If the field is required, prevent all white space input
        if (!field.required) {
          return true;
        }
        return value.trim().length !== 0 || field.message;
      }
    })} />;
  }
  return <_StyledDiv>
      {status.state === "SUCCESS" ? <SuccessMessage message={translations["message"][locale]} /> : <>
          {text && <_StyledHeadline type="h2" text={text} $_css={sizes[size]} />}
          <_StyledDiv2>
            <_StyledForm onFocusCapture={() => preloadRecaptcha()}>
              {fields.map((field, idx) => <_StyledDiv3 key={idx}>
                  {renderField(field)}
                </_StyledDiv3>)}
              <_StyledDiv4>
                <_StyledButton fullHeight onClick={onSubmit} variant="black" size="consistent" disabled={status.state === "LOADING"}>
                  {status.state === "LOADING" ? <_StyledIcon icon={Loading} size="24" /> : translations["submit"][locale]}
                </_StyledButton>
              </_StyledDiv4>
            </_StyledForm>
            {status.state === "ERROR" && status.message && <FieldError message={status.message} />}
          </_StyledDiv2>
        </>}
    </_StyledDiv>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-69vl1r-0"
})({
  "display": "flex",
  "width": "100%",
  "flexWrap": "wrap",
  "@media (min-width: 1024px)": {
    "alignItems": "center"
  }
});
var _StyledHeadline = _styled2(Headline).withConfig({
  componentId: "sc-sf-69vl1r-1"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-69vl1r-2"
})({
  "position": "relative",
  "marginTop": "1.6rem",
  "flex": "1 1 0%",
  "@media (min-width: 1024px)": {
    "marginTop": "0"
  }
});
var _StyledForm = _styled4("form").withConfig({
  componentId: "sc-sf-69vl1r-3"
})({
  "display": "flex",
  "gap": "var(--spacing-md)"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-69vl1r-4"
})({
  "width": "100%"
});
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-69vl1r-5"
})({
  "height": "5.6rem",
  "width": "12rem",
  "@media (min-width: 1024px)": {
    "height": "6.4rem"
  }
});
var _StyledButton = _styled7(Button).withConfig({
  componentId: "sc-sf-69vl1r-6"
})({
  "width": "100%"
});
var _StyledIcon = /*#__PURE__*/_styled8(Icon).withConfig({
  displayName: "newsletter___StyledIcon",
  componentId: "sc-o75df-0"
})({
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))"
});